import React, { useState, useEffect, useRef } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import bgLogin from '../../assets/images/bg_login.jpg';
import logoNexus from '../../assets/images/logo_nexus_small.png';
import { cognitoErrorMessage, APP_VERSION_LABEL } from '../../utils';
import * as s from './styled-forgot-password-email';

// Icones inline Lucide-style — reuso do mesmo padrao do /login.
const IMail = (
  <svg
    className="lead"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);
const IArrow = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);
const IBack = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);
const IKey = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="7.5" cy="15.5" r="3.5" />
    <path d="m10 13 9-9M17 7l3 3" />
  </svg>
);
const IAlert = (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4M12 16v.5" />
  </svg>
);

// Regex simples pra email (mesma do /login — duplicada pra nao
// acoplar). Aceita: a@b.c, user.name+tag@sub.domain.com.br
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RecuperarSenhaEmail = () => {
  const history = useHistory();
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Auto-focus no campo de email ao montar — operador chega ja
  // podendo digitar.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Preload do bg_login pra evitar flash preto (mesma logica do /login).
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = bgLogin;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const handleChange = (value) => {
    setUsername(value);
    if (error) setError('');
  };

  const submit = (e) => {
    e.preventDefault();
    if (loading) return;
    if (!username.trim()) {
      setError('Informe seu e-mail cadastrado.');
      return;
    }
    if (!EMAIL_RE.test(username)) {
      setError('Formato de e-mail inválido.');
      return;
    }
    setLoading(true);
    Auth.forgotPassword(username)
      .then(() => {
        // SPA navigation — proxima etapa pede o codigo enviado por email.
        history.push('/forgot-password/verify', { email: username });
      })
      .catch((e2) => {
        setLoading(false);
        setError(cognitoErrorMessage(e2));
      });
  };

  return (
    <s.Stage data-screen-label="01 Forgot Password">
      {/* LEFT — brand panel (escondido em <=1080px via styled). */}
      <s.BrandSide>
        <s.BrandBg $src={bgLogin} />
        <s.BrandOverlay />

        <s.BrandTop>
          <s.BrandMark>
            <img src={logoNexus} alt="Nexus" />
          </s.BrandMark>
        </s.BrandTop>

        <s.BrandHero>
          <s.Eyebrow>
            <span className="pulse" />
            Recuperação de acesso
          </s.Eyebrow>
          <s.HeroTitle>
            Recupere sua
            <br />
            <span className="accent">senha.</span>
          </s.HeroTitle>
          <s.HeroLead>
            Informe o e-mail cadastrado e enviaremos um código para criar
            uma nova senha. Em alguns segundos você está de volta ao painel.
          </s.HeroLead>
        </s.BrandHero>

        <s.BrandFoot>
          <span>© 2026 Higra. Todos os direitos reservados.</span>
        </s.BrandFoot>
      </s.BrandSide>

      {/* RIGHT — form */}
      <s.FormSide>
        <s.FormWrap>
          <s.FormHead>
            <s.FormHeadLbl>
              {IKey} Recuperar senha
            </s.FormHeadLbl>
            <s.FormTitle>Esqueceu sua senha?</s.FormTitle>
            <s.FormSub>
              Tudo bem, acontece. Informe seu e-mail abaixo e enviaremos um
              código para você criar uma nova.
            </s.FormSub>
          </s.FormHead>

          <s.Form onSubmit={submit}>
            <s.Field $error={!!error}>
              <s.FieldLabel>
                E-mail
                <span className="hint">cadastrado na conta</span>
              </s.FieldLabel>
              <s.Ctrl>
                {IMail}
                <input
                  ref={inputRef}
                  type="email"
                  placeholder="voce@empresa.com"
                  autoComplete="email"
                  value={username}
                  onChange={(e) => handleChange(e.target.value)}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'forgot-form-error' : undefined}
                />
              </s.Ctrl>
              {error && (
                <s.Hint id="forgot-form-error" role="alert">
                  {IAlert}
                  {error}
                </s.Hint>
              )}
            </s.Field>

            <s.Submit
              disabled={loading || !username}
              aria-busy={loading}
            >
              {loading ? (
                'Enviando…'
              ) : (
                <>
                  Enviar código {IArrow}
                </>
              )}
            </s.Submit>

            <s.BackToLogin
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                history.push('/login');
              }}
            >
              {IBack}
              Voltar ao login
            </s.BackToLogin>
          </s.Form>
        </s.FormWrap>

        <s.FormFoot>
          <s.Vers>{APP_VERSION_LABEL}</s.Vers>
        </s.FormFoot>
      </s.FormSide>
    </s.Stage>
  );
};

export default withRouter(RecuperarSenhaEmail);
